import React, { useState, useEffect, createRef } from 'react';
import { MDBRow, MDBCol } from 'mdbreact';
import { post } from 'axios';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { renderToast } from 'components/Informations/Toast/utils';
import { contentSvgs } from 'assets/icons';

import PageStructure from 'components/PageStructure';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';
import Overlay from 'components/Informations/Overlay';

import Checkbox from 'components/FormGroup/Checkbox';
import SelectField from 'components/FormGroup/SelectField';

import { formatDate } from 'utils/moment';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import { pdiIcons } from 'pages/PDI/utils';

import * as S from './styles';

const { getEmployeesToSelect, allPDIResults, publishResult } = endpoints;

const generateArrayRefs = (data, callback) =>
  Array.from({ length: data.length }, callback);

export default function Results({ user }) {
  const { id_funcionario: idFuncionario, administrador } = user;
  const isAdmin = administrador === 1;

  const [loading, setLoading] = useState(true);

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [checkedValues, setCheckedValues] = useState([]);
  const [checkboxRefs, setCheckboxRefs] = useState([]);

  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: 6000,
  });
  const [toastVisibility, setToastVisibility] = useState(false);

  const [seniorLevelOptions, setSeniorLevelOptions] = useState([]);
  const [selectedSeniorLevel, setSelectedSeniorLevel] = useState('');
  const [areaOptions, setAreaOptions] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [employeeOptions, setEmployeesOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [tableData, setTableData] = useState([]);
  const [searchTableData, setSearchTableData] = useState([]);
  const [tableOrder, setTableOrder] = useState('date');

  const [observer, setObserver] = useState(false);

  const [allEmployees] = useFetch(getEmployeesToSelect);
  const [allResults] = useFetch(`${allPDIResults}/${idFuncionario}`, observer);

  useEffect(() => {
    setLoading(true);

    (async () => {
      const response = await allResults;

      const employees = response.map(employee => ({
        value: employee.id_funcionario,
        label: employee.nome,
      }));

      const seniorLevels = _.uniqBy(
        response.map(level => ({
          value: level.senioridade,
          label: level.senioridade,
        })),
        'label',
      );

      const areas = _.uniqBy(
        response.map(area => ({
          value: area.descricao_area,
          label: area.descricao_area,
        })),
        'label',
      );

      setCheckboxRefs(generateArrayRefs(response, () => createRef()));
      setTableData(_.sortBy(response, date => new Date(date.dt_resultado)));
      setEmployeesOptions(_.orderBy(employees, 'label'));
      setSeniorLevelOptions(_.orderBy(seniorLevels, 'label'));
      setAreaOptions(_.orderBy(areas, 'label'));

      return response && employees && seniorLevels && areas
        ? setLoading(false)
        : setLoading(true);
    })();
  }, [allEmployees, allResults]);

  const handleCheck = (idIndex, data) => {
    const toJSON = JSON.parse(data);

    const { id_resultado: idResultado } = toJSON;

    const checkedPreviously = _.findIndex(checkedValues, { idIndex });

    if (checkedPreviously !== -1) {
      return setCheckedValues(current =>
        current.filter((__, index) => index !== checkedPreviously),
      );
    }

    return setCheckedValues(current => [
      ...current,
      {
        idIndex,
        data: {
          id_resultado: idResultado,
        },
        checked: true,
      },
    ]);
  };

  const overlayElement = (
    <Overlay
      isOpen={overlayVisibility}
      type="other"
      color="#32B88B"
      customSvg={pdiIcons('publish')}
      text={[
        <span>Publicar</span>,
        ' resultados? Ao publicar, o funcionário poderá acessar o resultado do seu PDI. ',
        'Essa ação não poderá ser desfeita.',
      ]}
    >
      <UnfilledButton
        text="Não, cancelar"
        handleClick={() => {
          setOverlayVisibility(false);
        }}
      />
      <FilledButton
        buttonClass="confirm"
        text="Sim, publicar"
        handleClick={() => {
          handleSubmit();
          setOverlayVisibility(false);
        }}
      />
    </Overlay>
  );

  const handleSubmit = async () => {
    setLoading(true);
    const resultIds = checkedValues.map(
      ({ data: { id_resultado: idResultado } }) => idResultado,
    );

    const objectToSend = {
      id_usr_up: idFuncionario,
      ids_resultados: resultIds,
    };

    try {
      await post(publishResult, objectToSend)
        .then(() => {
          setLoading(false);
          setObserver(current => !current);
          setCheckedValues([]);
        })
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Resultados publicados com sucesso',
            closeTime: 6000,
          });
        });
    } catch {
      setLoading(false);
      setToastVisibility(true);

      return setToastInfos({
        type: 'error',
        message: 'Falha na publicação. Tente novamente',
        closeTime: 6000,
      });
    }
  };

  const handleSearch = (searchType, searchedValue) => {
    setCheckedValues([]);

    const filterCondition = type => element => element[type] === searchedValue;

    const removeCleanOption = (originalArray, settedArray) => {
      const elementIndex = _.findIndex(originalArray, {
        label: 'LIMPAR FILTRO',
      });
      return elementIndex !== -1
        ? settedArray(currentElements =>
            currentElements.filter((__, index) => index !== elementIndex),
          )
        : null;
    };

    const removeAllFilters = () => {
      setSelectedArea('');
      setSelectedEmployee('');
      setSelectedSeniorLevel('');
      setSearchTableData([]);

      removeCleanOption(areaOptions, setAreaOptions);
      removeCleanOption(employeeOptions, setEmployeesOptions);
      removeCleanOption(seniorLevelOptions, setSeniorLevelOptions);
    };

    if (searchedValue === -1) return removeAllFilters();

    switch (searchType) {
      case 'area':
        setSelectedEmployee('');
        removeCleanOption(employeeOptions, setEmployeesOptions);

        setSelectedSeniorLevel('');
        removeCleanOption(seniorLevelOptions, setSeniorLevelOptions);

        if (searchedValue === -1) return removeAllFilters();

        setAreaOptions(currentAreas => [
          {
            label: 'LIMPAR FILTRO',
            value: -1,
          },
          ...currentAreas,
        ]);
        return setSearchTableData(
          tableData.filter(filterCondition('area')) || [],
        );

      case 'seniorLevel':
        setSelectedArea('');
        removeCleanOption(areaOptions, setAreaOptions);

        setSelectedEmployee('');
        removeCleanOption(employeeOptions, setEmployeesOptions);

        setSeniorLevelOptions(currentLevels => [
          {
            label: 'LIMPAR FILTRO',
            value: -1,
          },
          ...currentLevels,
        ]);
        return setSearchTableData(
          tableData.filter(filterCondition('senioridade')) || [],
        );

      case 'employee':
        setSelectedArea('');
        removeCleanOption(areaOptions, setAreaOptions);

        setSelectedSeniorLevel('');
        removeCleanOption(seniorLevelOptions, setSeniorLevelOptions);

        setEmployeesOptions(currentEmployees => [
          {
            label: 'LIMPAR FILTRO',
            value: -1,
          },
          ...currentEmployees,
        ]);
        return setSearchTableData(
          tableData.filter(filterCondition('id_funcionario')) || [],
        );

      default:
        return '';
    }
  };

  const renderTableElements = array =>
    array.map((lineItem, lineIndex) => {
      const checkIsChecked = index => {
        if (!checkedValues.length) return false;

        const checked = checkedValues.filter(
          ({ idIndex }) => idIndex === index,
        );

        if (checked && checked.length > 0) return true;

        return false;
      };

      return (
        <tr key={lineItem.id_pdi}>
          {isAdmin ? (
            <td>
              {lineItem.publicado ? (
                pdiIcons('check')
              ) : (
                <Checkbox
                  ref={checkboxRefs[lineIndex]}
                  onChange={() =>
                    handleCheck(lineIndex, JSON.stringify(lineItem))
                  }
                  id={`check-${lineIndex}`}
                  name={`check-${lineIndex}`}
                  checked={checkIsChecked(lineIndex)}
                />
              )}
            </td>
          ) : (
            <td> </td>
          )}
          <td>{`${formatDate(lineItem.dt_resultado)}`}</td>
          <td>{lineItem.nome}</td>
          <td>{lineItem.senioridade}</td>
          <td>{lineItem.media_senioridade}</td>
          <td>{lineItem.resultado_individual}</td>
        </tr>
      );
    });

  const toggleTableOrder = (orderBy, originalArray, settedArray) => {
    switch (orderBy) {
      case 'date':
        setTableOrder('date');
        return settedArray(
          _.sortBy(originalArray, date => new Date(date.dt_resultado)),
        );
      case 'results':
        setTableOrder('results');
        return settedArray(
          _.sortBy(originalArray, item => item.resultado_individual),
        );
      default:
        return null;
    }
  };

  return (
    <>
      {overlayVisibility && overlayElement}

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      <PageStructure
        title="Plano de Desenvolvimento Individual - Resultados"
        subtitle="Filtre por senioridade ou busque por funcionário para acessar os resultados do PDI."
      >
        <S.TableActions>
          <MDBRow>
            {isAdmin && (
              <MDBCol lg="4" sm="12">
                <SelectField
                  name="areas"
                  hasLabel
                  labelText="Área"
                  placeholderText="Selecione uma área..."
                  hasSecondaryStyle
                  value={
                    areaOptions.find(option => option.value === selectedArea) ||
                    ''
                  }
                  options={areaOptions}
                  handleChange={({ value }) => {
                    setSelectedArea(value);
                    handleSearch('area', value);
                  }}
                />
              </MDBCol>
            )}
            <MDBCol lg="4" sm="12">
              <SelectField
                name="seniorLevel"
                placeholderText="Selecione um nível..."
                hasLabel
                labelText="Senioridade"
                hasSecondaryStyle
                value={
                  seniorLevelOptions.find(
                    option => option.value === selectedSeniorLevel,
                  ) || ''
                }
                options={seniorLevelOptions}
                handleChange={({ value }) => {
                  setSelectedSeniorLevel(value);
                  handleSearch('seniorLevel', value);
                }}
              />
            </MDBCol>
            <MDBCol lg={isAdmin ? '4' : '5'} sm="12">
              <SelectField
                name="employees"
                placeholderText="Selecione um funcionário..."
                hasLabel
                labelText="Funcionário"
                hasSecondaryStyle
                value={
                  employeeOptions.find(
                    option => option.value === selectedEmployee,
                  ) || ''
                }
                options={employeeOptions}
                handleChange={({ value }) => {
                  setSelectedEmployee(value);
                  handleSearch('employee', value);
                }}
              />
            </MDBCol>
            {isAdmin && (
              <MDBCol lg="3" className="align-self-end mb-3">
                <FilledButton
                  text="Publicar resultado"
                  width={100}
                  handleClick={() => setOverlayVisibility(true)}
                  disabled={!checkedValues.length}
                  hasTooltip
                  tooltipText="Selecione um ou mais funcionários"
                />
              </MDBCol>
            )}
          </MDBRow>
        </S.TableActions>
        <MDBRow>
          <MDBCol lg="12">
            <S.Table isAdmin={isAdmin}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col"> </th>
                      <th
                        scope="col"
                        onClick={() => {
                          const currentTable = searchTableData.length
                            ? searchTableData
                            : tableData;
                          const settedTable = searchTableData.length
                            ? setSearchTableData
                            : setTableData;

                          toggleTableOrder('date', currentTable, settedTable);
                        }}
                      >
                        Data da avaliação
                        {tableOrder === 'date' && contentSvgs('tableArrow')}
                      </th>
                      <th scope="col">Funcionário</th>
                      <th scope="col">Senioridade</th>
                      <th scope="col">Média senioridade</th>
                      <th
                        scope="col"
                        onClick={() => {
                          const currentTable = searchTableData.length
                            ? searchTableData
                            : tableData;
                          const settedTable = searchTableData.length
                            ? setSearchTableData
                            : setTableData;

                          toggleTableOrder(
                            'results',
                            currentTable,
                            settedTable,
                          );
                        }}
                      >
                        Resultado individual
                        {tableOrder === 'results' && contentSvgs('tableArrow')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchTableData.length > 0
                      ? renderTableElements(searchTableData)
                      : renderTableElements(tableData)}
                  </tbody>
                </table>
              </div>
            </S.Table>
          </MDBCol>
        </MDBRow>
      </PageStructure>

      {loading && <ContentLoader isFull />}
    </>
  );
}

Results.defaultProps = {
  user: {},
};

Results.propTypes = {
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
